/**
 * Ctrl K: every live command, searchable, plus "search exercises for …" for anything
 * typed that is not a command. The fastest way to anything for someone who knows what
 * they want, and a map of what exists for someone who does not.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import clsx from 'clsx';
import { useEscapeLayer, useFocusTrap } from '../components/ui';
import { formatKeys, useRegistry, type Command } from './registry';
import { fuzzyScore } from './fuzzy';
import { tx } from '../i18n';

export { fuzzyScore } from './fuzzy';

export function CommandPalette() {
  const { paletteOpen, setPaletteOpen, commands, version } = useRegistry();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const close = () => setPaletteOpen(false);
  useEscapeLayer(paletteOpen, close, { modal: true });
  const dialog = useRef<HTMLDivElement>(null);
  useFocusTrap(dialog, paletteOpen);

  useEffect(() => {
    if (!paletteOpen) return;
    setQuery('');
    setIndex(0);
    requestAnimationFrame(() => input.current?.focus());
  }, [paletteOpen]);

  const results = useMemo(() => {
    if (!paletteOpen) return [];
    const live = commands().filter((c) => c.id !== 'palette.open');
    const scored = live
      .map((c) => ({ c, score: fuzzyScore(query, `${c.title} ${c.group} ${c.keywords ?? ''}`) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score || a.c.group.localeCompare(b.c.group));
    const list: Command[] = scored.map((r) => r.c);
    if (query.trim()) {
      list.push({
        id: 'palette.search',
        title: tx('Search exercises for “{query}”', { query: query.trim() }),
        group: tx('Search'),
        run: () => navigate(`/exercises?q=${encodeURIComponent(query.trim())}`),
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paletteOpen, query, version]);

  useEffect(() => setIndex(0), [query]);
  // the highlighted entry stays in view while the arrow keys walk past the fold
  useEffect(() => {
    if (paletteOpen) document.getElementById(`palette-${index}`)?.scrollIntoView({ block: 'nearest' });
  }, [index, paletteOpen]);
  if (!paletteOpen) return null;

  const run = (c: Command | undefined) => {
    if (!c) return;
    close();
    // after the palette is gone, so a command that opens a dialog gets the focus
    setTimeout(() => c.run(), 0);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4 pt-[12vh] backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && close()}
    >
      <div ref={dialog} role="dialog" aria-modal="true" aria-label={tx('Command palette')} className="card w-full max-w-xl overflow-hidden">
        <div className="flex items-center gap-2 border-b border-[var(--line)] px-3">
          <Search size={16} className="text-[var(--ink-faint)]" />
          <input
            ref={input}
            className="h-11 flex-1 bg-transparent text-sm outline-none"
            placeholder={tx('Type a command, or search exercises…')}
            value={query}
            aria-label={tx('Command')}
            aria-controls="palette-results"
            aria-activedescendant={results[index] ? `palette-${index}` : undefined}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setIndex((i) => Math.min(i + 1, results.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setIndex((i) => Math.max(i - 1, 0));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                run(results[index]);
              }
            }}
          />
        </div>
        <ul id="palette-results" role="listbox" className="max-h-[50vh] overflow-y-auto py-1">
          {results.length === 0 && <li className="px-3 py-6 text-center text-sm text-[var(--ink-faint)]">{tx('Nothing matches.')}</li>}
          {results.map((c, i) => (
            <li
              key={c.id}
              id={`palette-${i}`}
              role="option"
              aria-selected={i === index}
              onMouseEnter={() => setIndex(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                run(c);
              }}
              className={clsx(
                'flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm',
                i === index && 'bg-[var(--surface-muted)]',
              )}
            >
              <span className="min-w-0 truncate">
                <span className="mr-2 text-xs text-[var(--ink-faint)]">{c.group}</span>
                {c.title}
              </span>
              {c.keys && <Kbd keys={c.keys} />}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function Kbd({ keys }: { keys: string }) {
  return (
    <kbd className="shrink-0 rounded border border-[var(--line)] bg-[var(--surface-muted)] px-1.5 py-0.5 font-sans text-[11px] text-[var(--ink-soft)]">
      {formatKeys(keys)}
    </kbd>
  );
}

/** `?`: every shortcut that is live right now, grouped. */
export function ShortcutsOverlay() {
  const { overlayOpen, setOverlayOpen, commands, version } = useRegistry();
  useEscapeLayer(overlayOpen, () => setOverlayOpen(false), { modal: true });
  const dialog = useRef<HTMLDivElement>(null);
  useFocusTrap(dialog, overlayOpen);
  const groups = useMemo(() => {
    const map = new Map<string, Command[]>();
    for (const c of commands().filter((c) => c.keys)) map.set(c.group, [...(map.get(c.group) ?? []), c]);
    return [...map.entries()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayOpen, version]);
  if (!overlayOpen) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4 pt-[10vh] backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && setOverlayOpen(false)}
    >
      <div ref={dialog} role="dialog" aria-modal="true" aria-label={tx('Keyboard shortcuts')} className="card w-full max-w-2xl p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">{tx('Keyboard shortcuts')}</h2>
          <button className="btn btn-sm btn-ghost" onClick={() => setOverlayOpen(false)}>
            {tx('Close')}
          </button>
        </div>
        <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
          {groups.map(([group, list]) => (
            <section key={group}>
              <div className="label mb-1.5">{group}</div>
              <ul className="space-y-1">
                {list.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-[var(--ink-soft)]">{c.title}</span>
                    <Kbd keys={c.keys!} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <p className="mt-4 text-xs text-[var(--ink-faint)]">
          {tx('Single-key shortcuts are ignored while you type in a field. Everything here is also in the command palette.')}
        </p>
      </div>
    </div>
  );
}
